/* eslint-disable jsx-a11y/no-noninteractive-element-to-interactive-role */
import './style.modules.scss'

import {
  Button,
  ButtonVariant,
  MaterialIcon,
  MaterialIconSize,
  SidePanelContainer
} from '@ampcore/adel-react'
import { AmpcoreMaterialIconId } from '@ampcore/ampcom-contracts'
import { VAChatService } from '@ampcore/client-side-services'
import { AnalyticsTagBuilder } from '@dai-team/analytics-library'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { v4 as uuidv4 } from 'uuid'
import xss from 'xss'

import { Question } from './CustomerServiceSidePanel.type'
import {
  fetchIconName,
  getValueFromObject,
  handleClick,
  handleClickAccordion,
  transformData
} from './util'

export type CustomerServiceSidePanelProps = {
  testId?: string
}

export const CustomerServiceSidePanel: React.FC<
  CustomerServiceSidePanelProps
> = ({ testId }) => {
  const atb = useMemo(
    () => new AnalyticsTagBuilder().setDefaultName('customer-ssp-faqs'),
    []
  )

  const [questionList, setQuestionList] = useState<Question[]>([])
  const [heading, setHeading] = useState<string>('FAQs')
  const [menuTopics, setMenuTopics] = useState<
    Array<{ type?: string; [key: string]: unknown }>
  >([])
  const [chatId, setChatId] = useState<string>('')
  const [moreInformationOnMenuClick, setMoreInformationOnMenuClick] =
    useState<boolean>(false)
  const [hasError, setHasError] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const questionRefs = useRef<HTMLDivElement | null>(null)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [userId, _] = useState<string>(uuidv4())
  const [questionHeading, setQuestionHeading] = useState<string>('')
  const [showList, setShowList] = useState<boolean[]>([])
  const [loadingList, setLoadingList] = useState<boolean[]>([])
  const [showExtractedData, setExtractedData] = useState<string[]>([])
  const [isTriggerNodeHidden, setIsTriggerNodeHidden] = useState(false)

  useEffect(() => {
    const className = 'is-customerServiceSidePanel'
    document.body.classList.toggle(className, !isTriggerNodeHidden)

    return () => {
      document.body.classList.remove(className)
    }
  }, [isTriggerNodeHidden])

  const Loading = ({
    LoadingClassName = '',
    testId
  }: {
    LoadingClassName?: string
    testId?: string
  }) => (
    <div className={`Loading ${LoadingClassName}`} data-testid={testId}>
      <div className="Loading-indicator" />
      <div className="Loading-indicator" />
      <div className="Loading-indicator" />
    </div>
  )

  const Heading = (): JSX.Element => {
    const backButtonClassName = questionHeading
      ? 'Button Button--icon CustomerServiceSidePanel-heading-icon'
      : ''
    const headingWrapperLayout = questionHeading
      ? 'u-flex u-justifyContentCenter u-alignItemsCenter u-marginLeftM8'
      : 'u-textSizeMsrp1'
    const headingWrapper = questionHeading
      ? 'SidePanel-headingContent u-textSizeMsrp1 u-marginLeft8 u-marginTop4'
      : 'SidePanel-headingContent'
    return (
      <div className={`${headingWrapperLayout}`}>
        {questionHeading ? (
          <button
            className={backButtonClassName}
            title="Previous page in panel"
            onClick={() => resetStateOnClose('TEXT', 'CustServPage')}
          >
            <span className="u-hiddenVisually">Previous page in panel</span>
            <MaterialIcon
              icon={AmpcoreMaterialIconId.CHEVRON_LEFT}
              className="CustomerService-previous-icon"
            />
          </button>
        ) : (
          ''
        )}
        <h3 className={headingWrapper}>{heading}</h3>
      </div>
    )
  }

  const fetchData = async () => {
    const initialPayload = {
      userId: userId,
      userCtx: 'AMPFPUBLIC.UUID',
      msgType: 'ON_CONNECT',
      usrEmailAddr: userId + '@temp.com',
      vaChatId: '',
      val: ''
    }
    try {
      const service = new VAChatService()
      setIsLoading(true)
      const initialData = await service.send(initialPayload)
      const chatId = getValueFromObject(
        initialData,
        'd.val.results[0].messageId'
      )
      const chatIdStr = typeof chatId === 'string' ? chatId : ''
      if (chatIdStr === '') {
        /// if chat id is not available , we should not preoceed further
        return
      }
      setChatId(chatIdStr)
      const secondPayload = {
        userId: userId,
        userCtx: 'AMPFPUBLIC.UUID',
        msgType: 'TEXT',
        usrEmailAddr: userId + '@temp.com',
        vaChatId: chatIdStr,
        val: 'CustServPage'
      }
      const secondServiceCall = new VAChatService()

      const secondData = await secondServiceCall.send(secondPayload)
      setIsLoading(false)
      const menuItems = getValueFromObject(
        secondData,
        'd.val.results'
      ) as Array<{ type?: string; [key: string]: unknown }>
      // Use secondData for your transformed data, fallback to initialTexts if needed
      setMenuTopics(menuItems)
      setIsTriggerNodeHidden(true)
    } catch {
      setHasError(true)
    } finally {
      setIsLoading(false)
    }
  }

  function modifyQuestionSetsInfo(
    setQuestionHeading: (a: string) => void,
    defaultTopic: Question,
    setShowList: React.Dispatch<React.SetStateAction<boolean[]>>,
    questionList: Question[],
    setLoadingList: React.Dispatch<React.SetStateAction<boolean[]>>,
    setExtractedData: React.Dispatch<React.SetStateAction<string[]>>
  ) {
    // modifying question sets information for show , loading and extract data
    if (defaultTopic.payload) {
      setQuestionHeading(defaultTopic.payload)
    }
    setShowList(new Array(questionList.length).fill(false))
    setLoadingList(new Array(questionList.length).fill(false))
    setExtractedData(new Array(questionList.length).fill(''))
  }

  const renderQuestionList = () => {
    // Keyboard accessibility handler for Accordion-item
    const handleAccordionKeyDown = (
      e: React.KeyboardEvent<HTMLDetailsElement>,
      index: number,
      question: Question
    ) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        handleClickAccordion({
          e: e as unknown as React.MouseEvent<HTMLDetailsElement>,
          index,
          questionText: question.payload,
          loadingList,
          showExtractedData,
          setExtractedData,
          setLoadingList,
          showList,
          setShowList,
          userId,
          chatId
        })
      }
    }
    return (
      <>
        {questionHeading && (
          <p className="u-marginTop8 u-marginLeft24 CustomerServiceSidePanel-subHeading">
            {' '}
            <button
              className="Link"
              onClick={() => resetStateOnClose('TEXT', 'CustServPage')}
              data-testid={testId ? `${testId}-back-to-faqs-button` : undefined}
            >
              {' '}
              FAQs{' '}
              <MaterialIcon
                icon={AmpcoreMaterialIconId.CHEVRON_RIGHT}
                size={MaterialIconSize.SIZE_16}
                className="CustomerServiceSidePanel-resetIcon"
              />
            </button>
            {'  '}
            {questionHeading}
          </p>
        )}
        <h4 className="u-marginTop16 u-marginBottom20 u-marginLeft24 u-textBold">
          Frequently Asked Questions:
        </h4>

        <div
          className="Accordion u-marginLeft20 u-marginRight20"
          ref={questionRefs}
        >
          {questionList.length === 0 && showExtractedData[0] && (
            <div
              className="Accordion-content Content"
              dangerouslySetInnerHTML={{
                __html: xss(showExtractedData[0].replace(/\n/g, '<br/>'))
              }}
            ></div>
          )}
          {questionList.map((question, index) => {
            return (
              <details
                className="Accordion-item"
                key={question.payload}
                id={'Accordion-item' + ''}
                data-testid={
                  testId ? `${testId}-accordion-item-${index}` : undefined
                }
                open={showList[index] && Boolean(showExtractedData[index])}
                onClick={e =>
                  handleClickAccordion({
                    e,
                    index,
                    questionText: question.payload,
                    loadingList,
                    showExtractedData,
                    setExtractedData,
                    setLoadingList,
                    showList,
                    setShowList,
                    userId,
                    chatId
                  })
                }
                tabIndex={0}
                role="button"
                onKeyDown={e => handleAccordionKeyDown(e, index, question)}
              >
                <summary className="Accordion-toggle" id={'accordion' + index}>
                  {question.payload && (
                    <span className="Accordion-head">{question.payload}</span>
                  )}
                </summary>
                <div className="Accordion-content Content">
                  {loadingList[index] ? (
                    <Loading
                      testId={
                        testId ? `${testId}-loading-indicator` : undefined
                      }
                    />
                  ) : (
                    showExtractedData[index] && (
                      <div
                        className="accordion-extracted-data"
                        dangerouslySetInnerHTML={{
                          __html: xss(
                            showExtractedData[index].replace(/\n/g, '<br/>')
                          )
                        }}
                      ></div>
                    )
                  )}
                </div>
              </details>
            )
          })}
        </div>
      </>
    )
  }

  const menuClick = (
    event: React.MouseEvent<HTMLDivElement>,
    defaultTopic: Question
  ) => {
    handleClick(
      event,
      setQuestionList,
      setMoreInformationOnMenuClick,
      setHeading,
      setHasError,
      chatId,
      defaultTopic.payload,
      userId,
      setExtractedData
    )
    modifyQuestionSetsInfo(
      setQuestionHeading,
      defaultTopic,
      setShowList,
      questionList,
      setLoadingList,
      setExtractedData
    )
  }

  const renderMenuItems = (): JSX.Element => {
    // Keyboard accessibility handler for Card (must be after menuClick)
    const handleCardKeyDown = (
      e: React.KeyboardEvent<HTMLDivElement>,
      defaultTopic: Question
    ) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        // Synthesize a MouseEvent for menuClick
        const syntheticEvent = {
          ...e,
          preventDefault: e.preventDefault,
          stopPropagation: e.stopPropagation,
          currentTarget: e.currentTarget,
          target: e.target,
          nativeEvent: e.nativeEvent
        } as unknown as React.MouseEvent<HTMLDivElement>
        menuClick(syntheticEvent, defaultTopic)
      }
    }
    return (
      <div className="SidePanel-sectionsWrapper u-margin16 u-textSizeMsrm1">
        <h4 className="u-marginTop16 u-marginBottom16 u-textBold">
          Browse By Topic
        </h4>
        <div className="Grid Grid--withGutter" key="grid-menu-items">
          {menuTopics &&
            Array.isArray(menuTopics) &&
            transformData(menuTopics).map((defaultTopic: Question, key) => (
              <div
                className="u-size1of2 u-marginBottom16"
                key={`div-grid-item-${defaultTopic.title}`}
              >
                <div className="AdelComponent-gridItem">
                  <div
                    className="Card defaultTopicsMenu"
                    key={defaultTopic.title}
                    {...atb.dataAnalyticAttribute(defaultTopic.payload)}
                    onClick={(event: React.MouseEvent<HTMLDivElement>) =>
                      menuClick(event, defaultTopic)
                    }
                    data-testid={
                      testId ? `${testId}-menu-item-${key}` : undefined
                    }
                    tabIndex={0}
                    role="button"
                    onKeyDown={e => handleCardKeyDown(e, defaultTopic)}
                    aria-pressed="false"
                  >
                    <div className="Card-body-icon-container u-textNormal u-textCenter u-padding4">
                      {defaultTopic.title && (
                        <MaterialIcon
                          icon={
                            fetchIconName(
                              defaultTopic.title
                            ) as AmpcoreMaterialIconId
                          }
                          className="u-colorBrandIcon"
                        />
                      )}
                      <p className="u-marginTop4">{defaultTopic.payload}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          {isLoading &&
            !(
              menuTopics &&
              Array.isArray(menuTopics) &&
              menuTopics.length > 0
            ) && (
              <Loading
                testId={testId ? `${testId}-loading-indicator` : undefined}
                LoadingClassName={'u-posAbsoluteCenter'}
              />
            )}
        </div>
      </div>
    )
  }

  const resetStateOnClose = async (msgType: string, val: string) => {
    setHeading('FAQs')
    setMoreInformationOnMenuClick(false)
    setQuestionList([])
    setQuestionHeading('')
    const service = new VAChatService()

    const closePayload = {
      userId: userId,
      userCtx: 'AMPFPUBLIC.UUID',
      msgType: msgType,
      usrEmailAddr: userId + '@temp.com',
      vaChatId: chatId,
      val: val
    }
    try {
      await service.send(closePayload)
      if (msgType === 'SESSION_CLOSURE') {
        setIsTriggerNodeHidden(false)
      }
    } catch {
      /* Suppressing error appropriately */
      setHasError(true)
    }
  }

  return (
    <SidePanelContainer
      triggerNode={
        <Button
          className={`CustomerServiceSidePanel-triggerButton ${
            isTriggerNodeHidden ? 'u-hidden' : ''
          }`}
          variant={ButtonVariant.PRIMARY}
          {...atb.dataAnalyticAttribute('view-btn')}
          data-tracking="primary"
          onClick={() => fetchData()}
        >
          View FAQs
        </Button>
      }
      minimizable
      className="CustomerServiceSidePanelContainer"
      heading={<Heading />}
      handleCustomClose={() => resetStateOnClose('SESSION_CLOSURE', '')}
      testId={testId ? testId : undefined}
    >
      {hasError ? (
        <div
          className="Notification Notification--withIcon Notification--alert u-margin8"
          role="alert"
        >
          <div className="Notification-content">
            <p>An error occurred. Please try again later. </p>
          </div>
        </div>
      ) : null}
      {!hasError &&
        (!moreInformationOnMenuClick
          ? renderMenuItems()
          : renderQuestionList())}
    </SidePanelContainer>
  )
}
